import type { TBookAppointment } from "types";
import { newRequestId } from "./requestId";

type BookingBody = TBookAppointment & {
  payment_method_id: string;
  impersonate_id?: string;
  gift_card_codes?: string[];
};
export type PendingBooking = BookingBody & { booking_request_id: string };
const key = (userId: string) => `pointment-booking:${userId}`;
const storageRequired =
  "Allow browser storage before booking, so your request can be recovered if the connection is interrupted.";

export const getPendingBooking = (userId: string): PendingBooking | null => {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(key(userId));
  } catch {
    throw new Error(storageRequired);
  }
  if (!stored) return null;
  try {
    const request = JSON.parse(stored);
    if (
      !/^[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/i.test(
        request.booking_request_id
      ) ||
      typeof request.location_id !== "string" ||
      typeof request.payment_method_id !== "string" ||
      !Number.isFinite(new Date(request.start_time).getTime()) ||
      !Array.isArray(request.services) ||
      !request.services.length ||
      request.reschedule_appointment_id
    )
      throw new Error("Invalid request");
    return request;
  } catch {
    throw new Error(
      "We could not read your previous booking request. Contact the salon before booking again."
    );
  }
};

export const saveBookingRequest = (
  userId: string,
  body: BookingBody
): PendingBooking => {
  const existing = getPendingBooking(userId);
  if (existing) {
    // Recovery has its own button: never silently charge the old card/amount
    // while showing a newly edited checkout.
    throw new Error(
      "A previous booking still needs confirmation. Use Retry previous booking before making another reservation."
    );
  }
  const request = { ...body, booking_request_id: newRequestId() };
  const stored = JSON.stringify(request);
  try {
    sessionStorage.setItem(key(userId), stored);
    if (sessionStorage.getItem(key(userId)) !== stored)
      throw new Error("Not saved");
  } catch {
    throw new Error(storageRequired);
  }
  return request;
};

export const clearBookingRequest = (userId: string, requestId: string) => {
  try {
    if (getPendingBooking(userId)?.booking_request_id === requestId)
      sessionStorage.removeItem(key(userId));
  } catch {
    // Keeping the identity is safe: completion/rejection is replayable.
  }
};
